import React from 'react';
import { HiExternalLink } from 'react-icons/hi';
import HeroSection from '../components/ui/HeroSection';
import Section from '../components/ui/Section';
import ChalkboardBackground from '../components/ui/ChalkboardBackground';
import Button from '../components/ui/Button';
import siteContent from '../data/siteContent.json';
import homeBg from '../assets/images/bb-background.png';
import moonBg from '../assets/images/pink-moon-background.png';
import Seo from '../components/Seo';

const ContractPolicies = () => {
  const policies = [
    {
      id: 'booking-payment',
      title: 'Booking & Payment Policy',
      content: [
        'A signed contract is required to secure desired wedding date.',
        'A 50% non-refundable deposit of the total contract balance is due with the signed contract to reserve wedding day. The deposit fee will go towards the total contract balance.',
        'Contract and deposit is due within 7 days of the contract being issued. If the contract is not received within the 7 days, it is possible for another wedding to be booked on the desired date.',
        'All deposits and remaining balances for services booked must be paid through our business Venmo account: @BlushAndBobby.',
        'If you require a payment plan, options can be discussed ahead of time.',
        'The remaining 50% of the total contract balance is due 30 days before the wedding date.',
        {
          main: 'A final head count is due 30 days prior to your scheduled date.',
          sub: [
            'If you request additional services within 30 days of your scheduled date, the service will only be performed if time is allotted.',
            'Any services removed within 30 days of the scheduled date will be required to pay 100%; these services can be traded out for another person if they are wanting the same service.'
          ]
        }
      ]
    },
    {
      id: 'day-of-wedding',
      title: 'Day-of Wedding Policy',
      content: [
        'The bridal party must be present, prepared, and on time for all scheduled services. Delays may result in services being incomplete, and no refunds will be issued for missed or unfilled services.',
        'In the event that the stylist stands idle or is made to wait, a fee of $50 every 30 minutes will be charged per stylist.'
      ]
    },
    {
      id: 'trial-run',
      title: 'Trial Run Policy',
      content: [
        'While trial runs are optional, they are strongly recommended to ensure my style aligns with your desired look.',
        'There is a 24-hour cancellation policy and reschedule notice for all trial runs. If you fail to show up and my schedule does not permit a reschedule, you will still be responsible for paying 100% of the trial run.',
        'Trial runs are held on Sundays at my in-home studio located in Peculiar, Missouri; unless otherwise arranged.',
        'Bride is responsible for scheduling her trial run; I always do my best to follow up with you but is ultimately your responsibility to confirm and secure your appointment.'
      ]
    },
    {
      id: 'travel-assistant',
      title: 'Travel & Assistant Fee Policy',
      content: [
        'A minimum of 4 services is required for on-site travel.',
        'The travel fee is based on location of services being rendered and agreed upon before the approval of contract.',
        {
          main: 'If an additional assistant (beyond the two-stylist team) is required or requested, a minimum fee of $250 per assistant will be added to the contract total.',
          sub: [
            'The assistant fee may increase with each added service as I see fit (typically after 5 services), but will be agreed upon before the approval of contract.'
          ]
        },
        'If services are to be provided at more than one location, a minimum additional fee of $50 will be applied. This fee may increase depending on travel distance.'
      ]
    },
    {
      id: 'cancellation',
      title: 'Cancellation Policy',
      content: [
        'A cancellation of the wedding date must be made no later then 30 days before the scheduled date.',
        'Cancellations made within 30 days of the wedding date will require the bride to pay 100% of the total contract balance.',
        'Cancellations made more than 30 days in advance will result in the forfeiture of the non-refundable deposit, with no further balance due.',
        'In the unlikely event that Blush & Bobby is unable to provide services on the scheduled date, 100% of all payments made will be refunded.'
      ]
    },
    {
      id: 'other',
      title: 'Other Policies',
      content: [
        'By signing the contract, all clients receiving services agree to full use of photographs and videos taken before/during/after the services. The photos and videos may be used for the artists portfolio, promotional materials, or social media.',
        'We reserve the right to refuse any service for those who are ill, have lice, hair is too dirty or otherwise unsuitable for styling.',
        'Clients understand that weather, lighting, sweat, and other environmental factors may affect the longevity or appearance of services performed. These circumstances are not valid grounds for refunds or contract termination.',
        'We are not responsible for any allergic reactions or sensitivities to products used during services. It is the client\'s responsibility to inform the artist in advance of any known allergies, skin conditions, or sensitivities.'
      ]
    }
  ];

  return (
    <>
      <Seo
        title="Contract & Policies"
        description="Booking, payment, and cancellation policies for Blush & Bobby bridal hair and makeup services."
        path="/contract-policies"
        noindex
      />
      {/* Hero */}
      <HeroSection title="Contract Policies" backgroundImage={moonBg} opacity={1} overlayColor="bg-dusty-rose/25" />

      {/* Policies Accordion */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-gray-600 mb-8">
            Please review our contract policies below. Click on each section to expand and read the details.
          </p>
          <div className="space-y-4">
            {policies.map((policy) => (
              <div
                key={policy.id}
                className="border border-dusty-rose/20 rounded-2xl overflow-hidden bg-white shadow-sm"
              >
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none hover:bg-dusty-rose/5 transition-colors">
                    <span className="font-display text-xl font-semibold text-dusty-rose">
                      {policy.title}
                    </span>
                    <svg
                      className="h-6 w-6 text-dusty-rose transition-transform group-open:rotate-180"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>
                  <div className="px-6 pb-6 pt-2">
                    <ul className="space-y-3">
                      {policy.content.map((item, idx) => {
                        if (typeof item === 'object' && item.main) {
                          return (
                            <li key={idx} className="text-gray-700">
                              <div className="flex items-start">
                                <span className="text-dusty-rose mr-3 mt-1 font-bold">•</span>
                                <span>{item.main}</span>
                              </div>
                              {item.sub && item.sub.length > 0 && (
                                <ul className="mt-2 ml-6 space-y-2">
                                  {item.sub.map((subItem, subIdx) => (
                                    <li key={subIdx} className="text-gray-600 flex items-start">
                                      <span className="text-dusty-rose mr-3 mt-1">◦</span>
                                      <span>{subItem}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        }
                        return (
                          <li key={idx} className="text-gray-700 flex items-start">
                            <span className="text-dusty-rose mr-3 mt-1 font-bold">•</span>
                            <span>{item}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <ChalkboardBackground 
        as="section" 
        backgroundImage={homeBg}
        backgroundOpacity={0.5}
        className="py-12 md:py-16 lg:py-20"
      >
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold mb-6 text-chalk uppercase tracking-wider">
              Let's Create Your Dream Look
            </h2>
            <Button
              href={siteContent.social.linktree}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="lg"
              className="!text-xs"
            >
              Check Availability
              <HiExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </ChalkboardBackground>
    </>
  );
};

export default ContractPolicies;
